import {
	ALL_DOCUMENT_OPTIONS,
	ALL_ISSUANCE_APPLICATION_OPTION,
	DocumentTypeEnum,
	MasterInfoEnum,
	PageTypeEnum,
	TDocumentOption,
} from '@/constant';
import { useCommonList } from '@/hooks';
import { IProgram } from '@/services';
import {
	CommonActions,
	CommonSelectors,
	DefineDocumentAreaActions,
} from '@/stores';
import { DefineDocumentUploadActions } from '@/stores/define-document-upload';
import { insertObjectIfV2, sortByFieldAndLanguage } from '@/utils';
import {
	ForwardedRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DefaultOptionType } from 'antd/es/select';

import { TFilterWithViewAllRef } from '../common/filter-with-view-all/type';
import {
	DEFAULT_DOCUMENT_OPTION,
	DEFAULT_ISSUANCE_APPLICATION_OPTION,
} from '../define-document-area/define-document-area.config';
import { TSelectDocumentInfoProps } from './type';

const useLogic = (
	props: TSelectDocumentInfoProps,
	ref: ForwardedRef<TFilterWithViewAllRef>
) => {
	const {
		page,
		setPage,
		pageType,
		refTableId,
		refTableIds,
		getSelectedValue,
		fromDefineUpload,
		callApiMapping,
		listFranchiseMapping,
		listApplicantMapping,
		isDocumentArea,
		showProgram,
		getDefineDocumentUploadListCallback,
	} = props;
	const [isViewAll, setIsViewAll] = useState(true);
	const [document, setDocument] = useState(DEFAULT_DOCUMENT_OPTION[0].value);
	const [issuanceApplication, setIssuanceApplication] = useState(
		DEFAULT_ISSUANCE_APPLICATION_OPTION[0].value
	);
	const [applicantId, setApplicantId] = useState<number | string>('');
	const [franchiseId, setFranchiseId] = useState<number | string>('');
	const [programId, setProgramId] = useState<number | string>('');

	const [applicantMappingOptions, setApplicantMappingOptions] = useState<
		DefaultOptionType[]
	>([]);
	const [franchiseMappingOptions, setFranchiseMappingOptions] = useState<
		DefaultOptionType[]
	>([]);

	const headerProgram = useSelector(CommonSelectors.selectHeaderProgram);

	const { commonList: programList } = useCommonList(
		MasterInfoEnum.PROGRAM,
		showProgram
	);

	const programOptions = useMemo(() => {
		return programList?.data?.map((item: any) => ({
			value: item?.id,
			label: item?.name,
		}));
	}, [programList]);

	const headerProgramValue = useMemo(() => {
		if (showProgram) {
			return headerProgram?.value || '';
		}
		return '';
	}, [showProgram, headerProgram?.value]);

	const programCurrent = useMemo(() => {
		return programList?.data?.find(
			(item: any) => Number(item?.id) === Number(programId)
		) as IProgram;
	}, [programList?.data, programId]);

	const dispatch = useDispatch();

	const onReset = useCallback(() => {
		setIsViewAll(true);
		setDocument('');
		setIssuanceApplication('');
		setApplicantId('');
		setFranchiseId('');
		setProgramId('');
	}, []);

	useImperativeHandle(ref, () => {
		return {
			onReset,
			isSearching: !isViewAll,
		};
	});

	useEffect(() => {
		if (isDocumentArea) {
			setApplicantMappingOptions(listApplicantMapping || []);
			setFranchiseMappingOptions(listFranchiseMapping || []);
		}
	}, [listApplicantMapping, listFranchiseMapping, isDocumentArea]);

	useEffect(() => {
		setIsViewAll(
			document === '' &&
				issuanceApplication === '' &&
				applicantId === '' &&
				franchiseId === '' &&
				programId === ''
		);

		if (getSelectedValue) {
			return;
		}
		let params: DocumentTypeEnum[] = [];

		if (issuanceApplication === '' && document === '') {
			params = [];
		} else {
			if (document !== '') {
				params.push(document as DocumentTypeEnum);
			} else {
				documentOptions.forEach((item) => {
					if (item.value !== '') {
						params.push(item.value as DocumentTypeEnum);
					}
				});
			}
		}
		if (!fromDefineUpload && refTableId) {
			dispatch(
				DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
					page: 1,
					pageSize: page.pageSize,
					refTableName: pageType,
					refTableId: refTableId.toString(),
					documentType: params,
					...insertObjectIfV2(applicantId, {
						applicantId: Number(applicantId),
					}),
					...insertObjectIfV2(franchiseId, {
						franchiseId: Number(franchiseId),
					}),
					...insertObjectIfV2(programId, {
						programId: Number(programId),
					}),
				})
			);
		}

		if (fromDefineUpload && refTableId) {
			dispatch(
				DefineDocumentUploadActions.getDefineDocumentUploadListRequest({
					refTableName: pageType,
					refTableId,
					documentType: params,
					...insertObjectIfV2(applicantId, {
						applicantId: Number(applicantId),
					}),
					...insertObjectIfV2(franchiseId, {
						franchiseId: Number(franchiseId),
					}),
					...insertObjectIfV2(programId, {
						programId: Number(programId),
						programCurrent,
					}),
					callback: (error) => {
						getDefineDocumentUploadListCallback?.();
					},
				})
			);
		}
	}, [
		document,
		issuanceApplication,
		fromDefineUpload,
		pageType,
		refTableId,
		applicantId,
		franchiseId,
		programId,
		programCurrent,
	]);

	const isInitializedRef = useRef(false);

	useEffect(() => {
		if (isInitializedRef.current) {
			return;
		}

		if (
			(fromDefineUpload ? refTableId : refTableIds?.length) &&
			callApiMapping
		) {
			dispatch(
				CommonActions.getDocumentMappingsRequest({
					refTableName: pageType,
					refTableId: refTableIds || [],
					...insertObjectIfV2(fromDefineUpload, {
						refTableId: [refTableId],
						isDefineUpload: true,
					}),
					callback: (error, data) => {
						if (error) {
							return;
						} else {
							let applicantFilteredData = data?.APPLICANT || [];
							let franchiseFilteredData = data?.FRANCHISE || [];
							if (fromDefineUpload) {
								if (pageType === PageTypeEnum.APPLICANT) {
									applicantFilteredData = applicantFilteredData?.filter(
										(item) => item.value !== refTableId
									);
								}
								if (pageType === PageTypeEnum.FRANCHISE) {
									franchiseFilteredData = franchiseFilteredData?.filter(
										(item) => item.value !== refTableId
									);
								}
							}
							setApplicantMappingOptions(applicantFilteredData);
							setFranchiseMappingOptions(franchiseFilteredData);
						}
					},
				})
			);

			isInitializedRef.current = true;
		}
	}, [refTableId, refTableIds]);

	useEffect(() => {
		setProgramId(headerProgramValue);
	}, [headerProgramValue]);

	const toggleViewAll = useCallback(
		(e: CheckboxChangeEvent) => {
			if (isViewAll) {
				return;
			}

			setIsViewAll(e.target.checked); // Update state to reflect checkbox change.
			if (e.target.checked) {
				setIssuanceApplication('');
				setDocument('');
				setApplicantId('');
				setFranchiseId('');
				setProgramId('');
			}
		},
		[isViewAll, headerProgramValue]
	);

	const documentOptions = useMemo(() => {
		let documentFilter: TDocumentOption[] = [];
		if (issuanceApplication) {
			const issuanceApplicationSelected = ALL_ISSUANCE_APPLICATION_OPTION.find(
				(item) => item.value === issuanceApplication
			);

			if (issuanceApplicationSelected?.documents?.[pageType]) {
				documentFilter = ALL_DOCUMENT_OPTIONS.filter((item) =>
					issuanceApplicationSelected.documents[pageType].includes(item.value)
				);
			}
		} else {
			documentFilter = ALL_DOCUMENT_OPTIONS.filter((item) =>
				item.parents?.includes(pageType)
			);
		}
		const documents = documentFilter
			.map((item) => ({
				value: item.value,
				label: item.labelByPage?.[pageType] ?? item.label,
			}))
			.sort(sortByFieldAndLanguage('label'));

		return DEFAULT_DOCUMENT_OPTION.concat(documents);
	}, [issuanceApplication, pageType]);

	const issuanceApplicationOptions = useMemo(() => {
		const issuanceApplicationOptionsBaseOnPage =
			ALL_ISSUANCE_APPLICATION_OPTION.filter((item) =>
				item.pageType.includes(pageType)
			).map((item) => {
				return {
					value: item.value,
					label: item.label,
				};
			});
		return DEFAULT_ISSUANCE_APPLICATION_OPTION.concat(
			issuanceApplicationOptionsBaseOnPage
		);
	}, [pageType]);

	const handleIssuanceApplicationChange = useCallback((value: string) => {
		setIssuanceApplication(value);
		setDocument('');
		setPage((pre) => ({
			...pre,
			current: 1,
		}));
	}, []);

	const handleChangeApplicant = useCallback((value: string) => {
		setApplicantId(value);
		setPage((pre) => ({
			...pre,
			current: 1,
		}));
	}, []);

	const handleDocumentChange = useCallback((value: string) => {
		setDocument(value);
		setPage((pre) => ({
			...pre,
			current: 1,
		}));
	}, []);

	const handleChangeFranchise = useCallback((value: string) => {
		setFranchiseId(value);
		setPage((pre) => ({
			...pre,
			current: 1,
		}));
	}, []);

	const handleChangeProgram = useCallback((value: string) => {
		setProgramId(value);
		setPage((pre) => ({
			...pre,
			current: 1,
		}));
	}, []);

	useEffect(() => {
		if (getSelectedValue) {
			getSelectedValue({
				documentOptions,
				document: String(document),
				isAllData: isViewAll,
				applicantId,
				franchiseId,
			});
		}
	}, [
		getSelectedValue,
		documentOptions,
		document,
		isViewAll,
		applicantId,
		franchiseId,
	]);

	return {
		isViewAll,
		document,
		issuanceApplication,
		issuanceApplicationOptions,
		documentOptions,
		applicantId,
		franchiseId,
		applicantMappingOptions,
		franchiseMappingOptions,
		programId,
		programOptions,
		toggleViewAll,
		handleIssuanceApplicationChange,
		handleDocumentChange,
		handleChangeApplicant,
		handleChangeFranchise,
		handleChangeProgram,
	};
};

export default useLogic;
